import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Loader2, Send, Copy } from "lucide-react";

const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (minor, cur) => `${CUR_SYMBOL[cur] || cur + " "}${(Number(minor || 0) / 100).toFixed(2)}`;

const TABS = [
  { key: "preview", label: "Preview" },
  { key: "content", label: "Content" },
  { key: "recipient", label: "Recipient" },
];

/**
 * SubscriptionEmailPreviewModal — Phase 2B (step 2).
 *
 * Three tabs: PREVIEW (iframe of rendered HTML), CONTENT (subject + CTA
 * label + read-only summary), RECIPIENT (editable to email/name).
 * SEND performs the real provider subscription creation via the backend
 * and dispatches the branded email through the standard comms ledger.
 */
export default function SubscriptionEmailPreviewModal({
  open, onClose, businessId, subscriptionId, defaultTo, onSent,
}) {
  const [tab, setTab] = useState("preview");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [ctaLabel, setCtaLabel] = useState("");
  const [toEmail, setToEmail] = useState(defaultTo?.email || "");
  const [toName, setToName] = useState(defaultTo?.name || "");

  const loadPreview = async (overrides = {}) => {
    setLoading(true);
    try {
      const r = await hqApi.post(
        `/control/clients/${businessId}/subscriptions/${subscriptionId}/preview`,
        {
          cta_label: overrides.cta_label ?? ctaLabel ?? undefined,
          to_email: overrides.to_email ?? toEmail ?? undefined,
          to_name: overrides.to_name ?? toName ?? undefined,
        },
      );
      setData(r.data);
      if (r.data.to_email && !toEmail) setToEmail(r.data.to_email);
      if (r.data.to_name && !toName) setToName(r.data.to_name);
      if (r.data.cta_label && !ctaLabel) setCtaLabel(r.data.cta_label);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not render preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !subscriptionId) return;
    setTab("preview");
    setCtaLabel(""); setToEmail(defaultTo?.email || ""); setToName(defaultTo?.name || "");
    setData(null);
    loadPreview({ to_email: defaultTo?.email, to_name: defaultTo?.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscriptionId]);

  const send = async () => {
    if (!toEmail) return toast.error("Recipient email is required");
    setSending(true);
    try {
      const r = await hqApi.post(
        `/control/clients/${businessId}/subscriptions/${subscriptionId}/send`,
        { cta_label: ctaLabel || undefined, to_email: toEmail, to_name: toName },
      );
      const status = r.data.send_status || "sent";
      if (status === "sent") toast.success(`Email sent to ${r.data.to}`);
      else if (status === "skipped") toast.message(`Email queued (skipped locally — ${r.data.to})`);
      else toast.error(`Send failed: ${status}`);
      onSent?.(r.data);
    } catch (e) {
      // Phase 3I.3K — never pass a raw object into toast (sonner renders
      // it as a React child which crashes the whole HQ shell to a solid
      // orange screen). Coerce structured errors to a readable string.
      const d = e?.response?.data?.detail;
      const msg =
        typeof d === "string" ? d :
        (d?.message || d?.code || e?.message || "Send failed");
      toast.error(String(msg));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="hq-sub-preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Setup email</div>
            <h2 className="font-black text-lg mt-1">
              {data ? `${data.product_label} · ${data.plan_name}` : "Loading…"}
            </h2>
          </div>
          <button data-testid="hq-sub-preview-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]">
            <X size={16}/>
          </button>
        </div>

        <div className="flex gap-1 px-6 py-3 border-b border-[#EAEAEA] bg-[#F6F5F2]">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`hq-sub-preview-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${tab === t.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA]"}`}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3 text-[11px] text-[#4A4A4A]">
            {data && (
              <span>
                {money(data.amount_minor, data.currency)} / {data.cycle === "annual" ? "year" : "month"}
              </span>
            )}
            {data?.provider && (
              <span className="px-2 py-0.5 rounded-full bg-[#FFF7EE] border border-[#FCE1C7] font-black text-[9px] tracking-[0.16em] uppercase text-[#8A4A00]">
                {data.provider} · {data.provider_env}
              </span>
            )}
          </div>
        </div>

        {loading || !data ? (
          <div className="p-10 flex items-center justify-center"><Loader2 size={18} className="animate-spin"/></div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto">
            {tab === "preview" && (
              <div className="p-4">
                <iframe
                  title="Subscription setup email"
                  data-testid="hq-sub-preview-iframe"
                  srcDoc={data.html}
                  className="w-full h-[520px] border border-[#EAEAEA] rounded-xl bg-white"
                />
                {data.is_preview_placeholder && (
                  <div className="mt-2 text-[11px] text-[#8A6D00]">
                    Preview shows a placeholder link. The real secure payment URL is generated when you press SEND.
                  </div>
                )}
              </div>
            )}
            {tab === "content" && (
              <div className="p-6 space-y-4 text-sm">
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Subject</div>
                  <div className="px-3 py-2 rounded-lg bg-[#F6F5F2] font-bold">{data.subject}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">CTA label</div>
                  <input
                    data-testid="hq-sub-preview-cta"
                    value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)}
                    onBlur={() => loadPreview()}
                    className="w-full px-3 py-2 rounded-lg border uppercase font-black tracking-[0.14em] text-[12px]"
                    placeholder="Activate my plan"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <ReadOnly label="Plan" value={data.plan_name}/>
                  <ReadOnly label="Product" value={data.product_label}/>
                  <ReadOnly label="Billing" value={`${money(data.amount_minor, data.currency)} / ${data.cycle === "annual" ? "year" : "month"}`}/>
                  <ReadOnly label="Provider" value={`${data.provider} (${data.provider_env})`}/>
                </div>
              </div>
            )}
            {tab === "recipient" && (
              <div className="p-6 space-y-4 text-sm">
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">To name</div>
                  <input
                    data-testid="hq-sub-preview-to-name"
                    value={toName} onChange={(e) => setToName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border"
                  />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">To email</div>
                  <input
                    data-testid="hq-sub-preview-to-email"
                    value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                    type="email"
                    className="w-full px-3 py-2 rounded-lg border"
                  />
                </div>
                <p className="text-[11px] text-[#8A8A8A]">Nothing is sent until you press the SEND button below.</p>
              </div>
            )}
          </div>
        )}

        <div className="px-6 py-4 border-t border-[#EAEAEA] flex items-center justify-between gap-2">
          <div className="text-[11px] text-[#8A8A8A]">
            {data?.approve_url && !data.is_preview_placeholder && (
              <button
                data-testid="hq-sub-preview-copy"
                onClick={() => { navigator.clipboard?.writeText(data.approve_url); toast.success("Payment link copied"); }}
                className="inline-flex items-center gap-1 text-[#FF6A00] hover:underline"
              >
                <Copy size={12}/> Copy payment link
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="hq-sub-preview-cancel" onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">
              Close
            </button>
            <button
              data-testid="hq-sub-preview-send"
              onClick={send}
              disabled={sending || !data || !toEmail}
              className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${!sending && data && toEmail ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"}`}
            >
              {sending ? <Loader2 size={12} className="inline animate-spin mr-1"/> : <Send size={12} className="inline mr-1"/>}
              Send setup email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReadOnly({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      <div className="px-3 py-2 rounded-lg bg-[#F6F5F2] font-bold">{value || "—"}</div>
    </div>
  );
}
